import type { SimQuota } from "@/lib/api";

/**
 * The small print beside the SDK snippet: what a session costs and how many can run.
 *
 * The attempt count itself lives in the "Attempts" tile above, where the other numbers
 * are, and the runs themselves are the "My attempts" tab. This is what a student needs to
 * know *before* connecting, in particular the per-team concurrency limit, which is the
 * rule that produces an otherwise baffling 409 when a teammate is mid-run.
 */
export function SessionRules({ quota }: { quota: SimQuota }) {
  const allowed = quota.attempts_allowed;
  const exhausted = allowed !== null && quota.attempts_used >= allowed;
  const shared = quota.attempts_per === "team";

  return (
    <div className="space-y-3 text-xs">
      {exhausted ? (
        <p className="text-destructive">
          {shared ? "Your team has no attempts left." : "No attempts left."} Your best run is
          the one that counts, so nothing is lost — ask a TA if you think the cap should be
          raised.
        </p>
      ) : (
        <p className="text-muted-foreground">
          {quota.max_steps.toLocaleString()} steps per session,{" "}
          {quota.max_concurrent_sessions === 1
            ? "one session at a time per team"
            : `${quota.max_concurrent_sessions} sessions at a time per team`}
          {shared ? ", attempts shared by the team" : ""}. An abandoned or disconnected run
          still uses an attempt; only <code className="font-mono">checkin()</code> scores one.
        </p>
      )}
    </div>
  );
}
